import { useMutation } from 'react-relay';
import CREATE_RESPONSE_MUTATION, {
  type CreateResponseMutation,
} from '../../graphql/__generated__/CreateResponseMutation.graphql';

export type ResponseFormData = {
  content: string;
};

interface ValidationErrors {
  content?: string;
}

interface CreateResponseOptions {
  onSuccess?: (response: any) => void;
  onError?: (errors: string[]) => void;
}

const useCreateResponse = () => {
  const [commit, isPending] = useMutation<CreateResponseMutation>(CREATE_RESPONSE_MUTATION);

  const createResponse = async (
    postId: string,
    formData: ResponseFormData,
    options?: CreateResponseOptions,
    parentResponseId?: string,
  ) => {
    validateForm(formData);

    commit({
      variables: {
        postId,
        content: formData.content,
        parentResponseId: parentResponseId || null,
      },
      onCompleted: (response, errors) => {
        if (errors) {
          const errorMessages = errors.map((error) => error.message);
          options?.onError?.(errorMessages);
          return;
        }

        const result = response?.unified?.ai?.createResponseByPostId;

        if (result?.success && result.response) {
          options?.onSuccess?.(result.response);
        } else if (result?.errors && result.errors.length > 0) {
          const errorMessages = result.errors.filter((error) => error?.message).map((error) => error!.message);
          options?.onError?.(errorMessages);
        } else {
          options?.onError?.(['An unexpected error occurred']);
        }
      },
      onError: (error) => {
        options?.onError?.([error.message]);
      },
    });
  };

  return { createResponse, isPending };
};

export default useCreateResponse;

const validateForm = (data: ResponseFormData) => {
  const errors: ValidationErrors = {};

  if (!data.content.trim()) {
    errors.content = 'Content is required';
  }

  if (Object.keys(errors).length > 0) {
    throw errors;
  }
};
